import { IconButton, OdinIconSize } from '@myosh/odin-components';
import type { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import ConfirmActionToastComponent from '@/components/common/confirm-action-toast.component';
import { useAppDispatch } from '@/redux/hooks';
import { deleteRow, editRow, resetDraftRow, saveDraftRow } from '@/redux/slices/table/slice';
import type { Hazard } from '@/types/swms';

interface ActionCellProps extends CellContext<Hazard, void> {
  editMode?: boolean;
}

const ActionCell: ColumnDefTemplate<ActionCellProps> = ({ row, editMode }) => {
  const dispatch = useAppDispatch();

  const handleCancel = useCallback(() => {
    dispatch(resetDraftRow({ rowIndex: row.index }));
  }, [dispatch, row.index]);

  const handleSave = useCallback(() => {
    dispatch(saveDraftRow({ rowIndex: row.index }));
  }, [dispatch, row.index]);

  const handleEdit = useCallback(() => {
    dispatch(editRow({ rowIndex: row.index }));
  }, [dispatch, row.index]);

  const handleDelete = useCallback(() => {
    dispatch(deleteRow({ rowIndex: row.index }));
  }, [dispatch, row.index]);

  const handleConfirmDelete = useCallback(() => {
    toast(
      (props) => (
        <ConfirmActionToastComponent
          message="Are you sure you want to delete this row?"
          confirmActionLabel="Delete"
          onConfirmActionClick={handleDelete}
          {...props}
        />
      ),
      { autoClose: 10_000 }
    );
  }, [handleDelete]);

  return (
    <div className="flex justify-center gap-3">
      {editMode && (
        <>
          <IconButton
            onClick={handleCancel}
            icon="X"
            iconSize={OdinIconSize.ExtraSmall}
            iconClassName="cursor-pointer rounded-sm hover:bg-primary-5"
            tooltip="Cancel"
          />
          <IconButton
            onClick={handleSave}
            icon="Check"
            iconSize={OdinIconSize.ExtraSmall}
            iconClassName="cursor-pointer rounded-sm hover:bg-primary-5"
            tooltip="Save"
          />
        </>
      )}
      {!editMode && (
        <>
          <IconButton
            onClick={handleEdit}
            icon="Edit"
            iconSize={OdinIconSize.ExtraSmall}
            iconClassName="cursor-pointer rounded-sm hover:bg-primary-5"
            tooltip="Edit"
          />
          <IconButton
            onClick={handleConfirmDelete}
            icon="Trash"
            iconSize={OdinIconSize.ExtraSmall}
            iconClassName="cursor-pointer rounded-sm hover:bg-primary-5"
            tooltip="Delete"
          />
        </>
      )}
    </div>
  );
};

export default memo(ActionCell);
